import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface RowMenuItem {
  label: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
  /** Why it is disabled, or what it does. */
  title?: string;
}

/**
 * A "⋯" button that opens a short list of actions. The list is positioned
 * against the window, so a scrolling table around it cannot clip it; it
 * closes on a choice, a click elsewhere, Escape, or scrolling.
 */
export function RowMenu({ label, items }: { label: string; items: RowMenuItem[] }) {
  const [open, setOpen] = useState<{ top: number; right: number; upward: boolean } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(null);
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!menu.current?.contains(target) && !button.current?.contains(target)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
        button.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    // Focus the first item, for keyboard use.
    menu.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  function toggle() {
    if (open) {
      setOpen(null);
      return;
    }
    const rect = button.current!.getBoundingClientRect();
    // Open upward when there is not room for the list below.
    const upward = window.innerHeight - rect.bottom < 44 * items.length + 16;
    setOpen({ top: upward ? rect.top - 4 : rect.bottom + 4, right: window.innerWidth - rect.right, upward });
  }

  function onMenuKey(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const buttons = [...(menu.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])];
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'ArrowDown' ? (index + 1) % buttons.length : (index - 1 + buttons.length) % buttons.length;
    buttons[next]?.focus();
  }

  return (
    <>
      <button
        ref={button}
        type="button"
        className="btn btn-sm btn-ghost row-menu-button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={Boolean(open)}
        onClick={toggle}
      >
        ⋯
      </button>
      {open ? (
        <div
          ref={menu}
          className="row-menu"
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKey}
          style={{
            right: open.right,
            ...(open.upward ? { bottom: window.innerHeight - open.top } : { top: open.top }),
          }}
        >
          {items.map((item, index) => (
            <button
              key={index}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              title={item.title}
              onClick={() => {
                setOpen(null);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}
